import React, { useEffect, useRef, useState } from "react";
import img2 from "../assets/city-bg.jpg"

const FadeIn = ({ children, delay = 0, className = "" }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={ref}
            className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

export default function WhyUs() {
    const features = [
        { icon: "bi-bullseye", title: "Strategic", subtitle: "Approach" },
        { icon: "bi-people-fill", title: "Client", subtitle: "Focused" },
        { icon: "bi-award-fill", title: "Quality", subtitle: "Driven" },
        { icon: "bi-graph-up-arrow", title: "Results", subtitle: "Oriented" },
    ];

    return (
        <section className="relative min-h-[600px] md:min-h-[700px] flex flex-col justify-end overflow-hidden">
            {/* Background image with fallback gradient */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${img2})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-900/50" />

            {/* Content */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20 text-center">
                <FadeIn>
                    <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20 mb-6">
                        <i className="bi bi-stars" aria-hidden="true" />
                        Why Choose Us
                    </span>
                </FadeIn>

                <FadeIn delay={100}>
                    <h2 className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-4 font-heading">
                        Innovate <span className="text-amber-500">•</span> Integrate <span className="text-amber-500">•</span> Transform
                    </h2>
                </FadeIn>

                <FadeIn delay={200}>
                    <p className="text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
                        Driving digital transformation across East Africa with solutions that actually work.
                    </p>
                </FadeIn>
            </div>

            {/* Feature Cards */}
            <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pb-6">
                <FadeIn delay={300}>
                    <div className="grid grid-cols-2 md:grid-cols-4 rounded-2xl overflow-hidden bg-slate-900/80 backdrop-blur-xl border border-slate-800 shadow-2xl">
                        {features.map((item, index) => (
                            <div
                                key={item.title}
                                className={`group flex flex-col items-center text-center px-4 py-8 md:py-10 transition-all duration-300 hover:bg-slate-800/50 ${index < features.length - 1 ? "border-r border-slate-800" : ""
                                    }`}
                            >
                                <div className="w-14 h-14 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 text-2xl mb-4 group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-slate-950 transition-all duration-300">
                                    <i className={`bi ${item.icon}`} aria-hidden="true" />
                                </div>
                                <h3 className="text-base font-bold text-white mb-0.5">{item.title}</h3>
                                <p className="text-sm text-slate-400">{item.subtitle}</p>
                            </div>
                        ))}
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}